import { useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { get } from "@/api";
import {
  getAwardedReferralRewards,
  ReferralRewardsAchievements,
  ReferralRewardsAcknowledgeRewardsModal,
  ReferralRewardsReferralHistory,
} from "@/modules/referralRewards";
import TopNav from "@/molecules/navbars/TopNav";
import { Models } from "api/types";

type ReferralRewardsContainerProps = {
  platform: Models<"PlatformModel">;
  referralReward?: Models<"DisplayAvailableReferralReward">;
  referralHistoryForPerson?: Models<"DisplayReferralHistoryForPerson">;
  achievementProgress?: Models<"DisplayAchievementProgress">;
};

export const ReferralRewardsContainer = ({
  platform,
  referralReward,
  referralHistoryForPerson: initialReferralHistoryForPerson,
  achievementProgress: initialAchievementProgress,
}: ReferralRewardsContainerProps) => {
  const navigate = useNavigate();

  const { data: user } = useQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
  });

  const { data: referralHistoryForPerson } = useQuery({
    queryKey: [
      "/{platformId}/referral-history/{personId}",
      platform.id,
      user?.id,
    ],
    queryFn: () =>
      get("/incentives/{platformId}/referral-history/{personId}", {
        platformId: platform.id || "",
        personId: user?.id || "",
      }),
    enabled: !!platform.id && !!user?.id,
    initialData: initialReferralHistoryForPerson,
    onError: () => null,
  });

  const { data: achievementProgress } = useQuery({
    queryKey: [
      "/{platformId}/referral-achievements/{personId}",
      platform.id,
      user?.id,
    ],
    queryFn: () =>
      get("/incentives/{platformId}/referral-achievements/{personId}", {
        platformId: platform.id || "",
        personId: user?.id || "",
      }),
    enabled: !!platform.id && !!user?.id,
    initialData: initialAchievementProgress,
    onError: () => null,
  });

  const { data: awardedReferralRewardsResponse } = useQuery({
    queryKey: getAwardedReferralRewards.key({
      platformId: platform.id || "",
      personId: user?.id || "",
    }),
    queryFn: () =>
      getAwardedReferralRewards({
        platformId: platform.id || "",
        personId: user?.id || "",
      }),
    onError: () => null,
  });

  const { data: activeSignupReward } = useQuery({
    queryKey: ["/incentives/{platformLocationId}/active-signup-reward"],
    queryFn: () =>
      get("/incentives/{platformLocationId}/active-signup-reward", {
        platformLocationId: user?.platformLocation?.id ?? "",
      }),
    onError: () => null,
  });

  const { data: theme } = useQuery({
    queryKey: ["/platform/theme"],
    queryFn: () => get("/platform/theme"),
  });

  const hasBonusRewardActive =
    referralReward && activeSignupReward
      ? referralReward?.bonusRewardTitle.text
      : undefined;

  const isCashOrRepeatCash =
    referralReward?.referralRewardType === "RepeatCash";

  const shouldShowAchievements = isCashOrRepeatCash;

  return (
    <div className="ReferralRewardsContainer flex h-full flex-col bg-gray-50">
      <TopNav startIconSVG={ChevronLeft} onStartIconClick={() => navigate(-1)}>
        <div className="flex max-h-[32px] items-center">
          <h1 className="whitespace-nowrap text-xl">Track Referrals</h1>
        </div>
      </TopNav>

      {shouldShowAchievements && (
        <ReferralRewardsAchievements
          brandColor={theme?.brandColor ?? "#000000"}
          achievementProgress={achievementProgress ?? undefined}
          redeemableAwardedReferralRewards={
            awardedReferralRewardsResponse?.redeemableAwardedReferralRewards ||
            []
          }
          nextBonusRewardTitle={hasBonusRewardActive}
          referralReward={referralReward}
        />
      )}

      <ReferralRewardsReferralHistory
        history={referralHistoryForPerson?.referralHistory ?? []}
      />

      <ReferralRewardsAcknowledgeRewardsModal
        platformId={platform.id}
        onCloseAfterInteracting={() => null}
      />
    </div>
  );
};
